import styled from "styled-components";
import COLORS from "shared/constants/colors";

const Container = styled.div`
  padding: 0 20px 40px;
`;

const CartLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;
  margin-top: 10px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const CartMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CartPageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-family: "Inter600";
  color: #18191f;
`;

const CartPageSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  font-family: "Inter600";
  letter-spacing: 0.08em;
  color: #6b7280;
`;

const CartItemCard = styled.article`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) auto;
  gap: 20px;
  padding: 20px;
  background: ${COLORS.WHITE};
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    grid-template-columns: 88px minmax(0, 1fr);
    grid-template-rows: auto auto;
  }
`;

const CartItemImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background:
    linear-gradient(45deg, #eceff1 25%, transparent 25%),
    linear-gradient(-45deg, #eceff1 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #eceff1 75%),
    linear-gradient(-45deg, transparent 75%, #eceff1 75%);
  background-size: 12px 12px;
  background-position:
    0 0,
    0 6px,
    6px -6px,
    -6px 0;

  @media (max-width: 768px) {
    width: 88px;
    height: 88px;
  }
`;

const CartItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const CartItemTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-family: "Inter600";
  color: #18191f;
`;

const CartItemLine = styled.p`
  margin: 0;
  font-size: 14px;
  color: #374151;
  font-family: "Inter400";
`;

const CartItemOptions = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CartItemOption = styled.li`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-family: "Inter500";
  color: #166534;

  svg {
    font-size: 16px;
    color: #22c55e;
  }
`;

const CartItemLink = styled.a`
  color: ${COLORS.BLUE};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CartItemTextButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-left: 6px;
  color: ${COLORS.BLUE};
  font-size: 14px;
  font-family: "Inter400";
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const AddSizeLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-top: 4px;
  color: ${COLORS.BLUE};
  font-size: 14px;
  font-family: "Inter400";
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const CartItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: 120px;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const CartItemActionsTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const QuantityControl = styled.div`
  display: inline-flex;
  align-items: center;
`;

const QuantityInput = styled.input`
  width: 64px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
  font-family: "Inter500";
  color: #18191f;
  background: ${COLORS.WHITE};
  padding: 0 8px;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CartItemPricing = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const UnitPrice = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-family: "Inter400";
`;

const LineTotal = styled.span`
  font-size: 16px;
  font-family: "Inter600";
  color: #18191f;
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #374151;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const EditButton = styled(RemoveButton)`
  &:hover {
    color: ${COLORS.BLUE};
  }
`;

const CartSidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 20px;
`;

const SidebarCard = styled.div`
  background: ${COLORS.WHITE};
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const CheckoutCard = styled(SidebarCard)`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TotalLabel = styled.span`
  font-size: 18px;
  font-family: "Inter600";
  color: #18191f;
`;

const TotalAmount = styled.span`
  font-size: 22px;
  font-family: "Inter600";
  color: #18191f;
`;

const SidebarItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid #e5e7eb;
  padding-top: 14px;
`;

const SidebarItem = styled.li`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const SidebarItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const SidebarItemName = styled.span`
  font-size: 13px;
  font-family: "Inter500";
  color: #18191f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SidebarItemMeta = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-family: "Inter400";
`;

const SidebarItemPrice = styled.span`
  font-size: 13px;
  font-family: "Inter600";
  color: #18191f;
  white-space: nowrap;
`;

const CheckoutNote = styled.p`
  margin: 0;
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
  font-family: "Inter400";
`;

const MinOrderAlert = styled.div`
  margin: 0;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  font-family: "Inter500";
  line-height: 1.4;
`;

const EmptyCartMessage = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  font-family: "Inter400";
`;

const EmptyCartCard = styled.div`
  background: ${COLORS.WHITE};
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 40px 24px;
  text-align: center;
`;

export {
  Container,
  CartLayout,
  CartMain,
  CartPageTitle,
  CartPageSubtitle,
  CartItemCard,
  CartItemImage,
  CartItemBody,
  CartItemTitle,
  CartItemLine,
  CartItemOptions,
  CartItemOption,
  CartItemLink,
  CartItemTextButton,
  AddSizeLink,
  CartItemActions,
  CartItemActionsTop,
  QuantityControl,
  QuantityInput,
  CartItemPricing,
  UnitPrice,
  LineTotal,
  RemoveButton,
  EditButton,
  CartSidebar,
  CheckoutCard,
  TotalRow,
  TotalLabel,
  TotalAmount,
  SidebarItemList,
  SidebarItem,
  SidebarItemInfo,
  SidebarItemName,
  SidebarItemMeta,
  SidebarItemPrice,
  CheckoutNote,
  MinOrderAlert,
  EmptyCartMessage,
  EmptyCartCard,
};
